import { Component } from './component.js';

/**
 * Custom element for managing product item selection from metafields.
 * Supports selecting items and calculating total price.
 */
class ProductMetafieldListComponent extends Component {
  #selectedItems = new Map();

  /** @type {HTMLInputElement[]} */
  #checkboxes = [];

  /** @type {HTMLFormElement | null} */
  #productForm = null;

  /** @type {HTMLInputElement | null} */
  #propertyInput = null;

  /** @type {HTMLElement | null} */
  #totalContainer = null;

  /** @type {HTMLElement | null} */
  #totalElement = null;

  /** @type {HTMLButtonElement[]} */
  #zoomTriggers = [];

  /** @type {Intl.NumberFormat} */
  #currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  #handleFormInteraction = () => {
    this.#updateFormFields();
  };

  connectedCallback() {
    this.#cacheElements();
    this.#checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', this.#handleItemChange);
    });
    this.#zoomTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', (event) => this.#handleZoomTriggerClick(event, index));
    });

    // Form markup can appear right after this component depending on block order.
    if (!this.#setupFormWatcher()) {
      requestAnimationFrame(() => {
        this.#setupFormWatcher();
      });
    }
  }

  disconnectedCallback() {
    this.#checkboxes.forEach((checkbox) => {
      checkbox.removeEventListener('change', this.#handleItemChange);
    });
    this.#zoomTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', (event, index) => this.#handleZoomTriggerClick(event, index));
    });
    this.#clearFormWatcher();
  }

  #cacheElements() {
    this.#checkboxes = Array.from(this.querySelectorAll('input[type="checkbox"]'));
    this.#propertyInput = this.querySelector('.product-metafield-list__property-input');
    this.#totalContainer = this.querySelector('.product-metafield-list__total');
    this.#totalElement = this.querySelector('.product-metafield-list__total-price');
    this.#zoomTriggers = Array.from(this.querySelectorAll('.product-metafield-list__image-zoom-trigger'));

    // In collapsible mode the heading/total can live outside this custom element.
    if (!(this.#totalContainer instanceof HTMLElement) || !(this.#totalElement instanceof HTMLElement)) {
      const wrapper = this.closest('.product-metafield-list__wrapper');
      if (wrapper instanceof HTMLElement) {
        this.#totalContainer = wrapper.querySelector('.product-metafield-list__total');
        this.#totalElement = wrapper.querySelector('.product-metafield-list__total-price');
      }
    }
  }

  #setupFormWatcher() {
    const productFormId = this.dataset.productFormId;
    if (!productFormId) return false;

    const form = document.getElementById(productFormId);
    if (!(form instanceof HTMLFormElement)) return false;

    this.#productForm = form;

    form.addEventListener('submit', this.#handleFormInteraction);

    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (form.querySelectorAll('button[type="submit"]'));
    buttons.forEach((btn) => {
      btn.addEventListener('click', this.#handleFormInteraction);
    });

    return true;
  }

  #clearFormWatcher() {
    if (this.#productForm) {
      this.#productForm.removeEventListener('submit', this.#handleFormInteraction);
      const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (
        this.#productForm.querySelectorAll('button[type="submit"]')
      );
      buttons.forEach((btn) => {
        btn.removeEventListener('click', this.#handleFormInteraction);
      });
    }

    this.#productForm = null;
  }

  /** @param {Event} event */
  #handleItemChange = (event) => {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    const itemId = checkbox.dataset.itemId;
    const itemPrice = Number(checkbox.dataset.itemPrice);
    const itemTitle = checkbox.dataset.itemTitle;

    if (!itemId || !itemTitle || !Number.isFinite(itemPrice)) return;

    if (checkbox.checked) {
      this.#selectedItems.set(itemId, {
        price: itemPrice,
        title: itemTitle,
      });
    } else {
      this.#selectedItems.delete(itemId);
    }

    this.#updateTotalPrice();
    this.#updateFormFields();

    this.dispatchEvent(
      new CustomEvent('items:updated', {
        detail: {
          selectedItems: Array.from(this.#selectedItems.values()),
          totalPrice: this.#calculateTotalPrice(),
        },
        bubbles: true,
      })
    );
  };

  #updateTotalPrice() {
    const totalPrice = this.#calculateTotalPrice();
    if (this.#totalContainer instanceof HTMLElement && this.#totalElement instanceof HTMLElement) {
      if (totalPrice > 0) {
        this.#totalElement.textContent = this.#formatPrice(totalPrice);
        this.#totalContainer.hidden = false;
      } else {
        this.#totalElement.textContent = '';
        this.#totalContainer.hidden = true;
      }
    }
  }

  #calculateTotalPrice() {
    let total = 0;
    this.#selectedItems.forEach((item) => {
      total += item.price;
    });
    return total;
  }

  /** @param {number} price */
  #formatPrice(price) {
    return this.#currencyFormatter.format(price / 100);
  }

  #serializeSelectedItemTitles() {
    return Array.from(this.#selectedItems.values())
      .map((item) => item.title)
      .join(', ');
  }

  #updateFormFields() {
    if (!(this.#propertyInput instanceof HTMLInputElement)) return;

    if (this.#selectedItems.size > 0) {
      this.#propertyInput.value = this.#serializeSelectedItemTitles();
      this.#propertyInput.disabled = false;
    } else {
      this.#propertyInput.value = '';
      this.#propertyInput.disabled = true;
    }
  }

  /** @param {MouseEvent} event */
  /** @param {number} index */
  #handleZoomTriggerClick = (event, index) => {
    event.preventDefault();
    event.stopPropagation();

    const zoomDialog = this.querySelector('zoom-dialog');
    if (!zoomDialog) return;

    // Call the zoom-dialog component's open method
    zoomDialog.open(index, event);

    // Focus the close button after dialog renders
    requestAnimationFrame(() => {
      const closeButton = zoomDialog.querySelector('.dialog-zoomed-gallery__close-button');
      if (closeButton instanceof HTMLElement) {
        closeButton.focus();
      }
    });
  };

  getSelectedItems() {
    return Array.from(this.#selectedItems.values());
  }

  getSelectedItemsTotalPrice() {
    return this.#calculateTotalPrice();
  }
}

if (!customElements.get('product-metafield-list-component')) {
  customElements.define('product-metafield-list-component', ProductMetafieldListComponent);
}

export { ProductMetafieldListComponent };
